import { useParams } from "react-router-dom";
import { Snowflake, ShieldCheck } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { LeadsBrowser } from "../leads/LeadsBrowser";
import { KpiStrip, KpiTile } from "@/shared/components/ui/kpi";
import { EmptyState } from "@/shared/components/ui/empty-state";
import { useFreezeAudience } from "../use-audience";

/**
 * Audience (screens.md §7) — freeze the approved leads into a snapshot. Later
 * edits never change what was sent. The suppression gate re-runs fail-closed at
 * freeze: the last barrier before enrolling.
 */
export function AudienceTab() {
  const { id: campaignId } = useParams<{ id: string }>();
  const freeze = useFreezeAudience(campaignId ?? "");
  const result = freeze.data;

  return (
    <div className="space-y-6">
      <Card className="flex flex-wrap items-center gap-3 p-[18px]">
        <ShieldCheck className="h-[16px] w-[16px] text-ge-accent" strokeWidth={1.8} />
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 text-[14px] font-semibold text-ge-primary">
            Freeze the audience
            <InfoHint text="Snapshots the approved leads so later edits never change what was sent. Suppression re-runs here (fail-closed) — the last check before anyone is enrolled." />
          </p>
          <p className="text-[12.5px] text-ge-secondary">
            Snapshots the approved leads. Suppression re-runs here (fail-closed) — already-registered
            gyms are dropped at the gate before anything is enrolled.
          </p>
        </div>
        <Button data-testid="run-freeze" disabled={freeze.isPending || !campaignId} onClick={() => freeze.mutate()}>
          <Snowflake className="h-[14px] w-[14px]" strokeWidth={2.2} />
          {freeze.isPending ? "Freezing…" : "Freeze audience"}
        </Button>
      </Card>

      {freeze.isError && (
        <p className="text-[12px] text-ge-danger" data-testid="freeze-error">
          Couldn&apos;t freeze the audience. Make sure there are approved leads and try again.
        </p>
      )}

      {result && (
        <div className="space-y-3" data-testid="freeze-result">
          <KpiStrip>
            <KpiTile label="Audience size" value={String(result.audienceSize)} highlighted />
            <KpiTile label="Suppressed at gate" value={String(result.suppressed)} />
          </KpiStrip>
          <p className="text-[12px] text-ge-muted">
            {result.audienceSize} lead{result.audienceSize === 1 ? "" : "s"} frozen into the audience.
            {result.suppressed > 0
              ? ` ${result.suppressed} were dropped by the suppression gate.`
              : " Nothing was caught by the suppression gate."}{" "}
            Next: author copy and build the sequence.
          </p>
        </div>
      )}

      {!result && !freeze.isPending && !freeze.isError && (
        <EmptyState message="Freeze the audience to snapshot approved leads. This is the last barrier before enrolling — suppression re-runs fail-closed here." />
      )}

      {/* Affected leads (ADR-0025) — the approved leads being frozen, visible right here. */}
      <section className="space-y-3" data-testid="audience-leads">
        <p className="ge-eyebrow flex items-center gap-1">
          APPROVED LEADS
          <InfoHint text="The approved leads this freeze snapshots into the audience. Open any one to see its full profile." />
        </p>
        <LeadsBrowser campaignId={campaignId ?? ""} initialStatus="APPROVED" />
      </section>
    </div>
  );
}
